import { motion, useReducedMotion } from 'motion/react';
import { PaperPlaneTilt, Robot, EnvelopeOpen, CheckCircle } from '@phosphor-icons/react';

const LOOP = 11;

/* All elements share one LOOP duration with per-element `times`, so the whole
   sequence stays in sync without any React state driving it.

   The keyframe offsets must cover the full 0–1 range: WAAPI fills any gap
   before the first offset (or after the last) from the element's underlying
   style — fully visible — which makes every element flash on screen at the
   start of the loop. Pad each track to hold its first/last frame instead. */
function seq(reduce, frames, times) {
  if (reduce) return {};
  const padStart = times[0] > 0;
  const padEnd = times[times.length - 1] < 1;
  const padded = {};
  for (const key of Object.keys(frames)) {
    const vals = frames[key];
    padded[key] = [
      ...(padStart ? [vals[0]] : []),
      ...vals,
      ...(padEnd ? [vals[vals.length - 1]] : []),
    ];
  }
  return {
    animate: padded,
    transition: {
      duration: LOOP,
      repeat: Infinity,
      ease: 'easeInOut',
      times: [...(padStart ? [0] : []), ...times, ...(padEnd ? [1] : [])],
    },
  };
}

function Connector({ reduce, drawAt }) {
  return (
    <div className="relative ml-[33px] h-8 w-px">
      <motion.div
        className="absolute inset-0 origin-top bg-gradient-to-b from-indigo-500/60 to-indigo-400/30"
        {...seq(reduce, { scaleY: [0, 1, 1, 0] }, [drawAt, drawAt + 0.04, 0.93, 0.98])}
      />
      <motion.div
        className="absolute -left-[3px] top-0 h-[7px] w-[7px] rounded-full bg-indigo-400 shadow-[0_0_8px_rgba(129,140,248,0.9)]"
        {...seq(
          reduce,
          { opacity: [0, 1, 1, 0, 0], y: [0, 2, 26, 28, 0] },
          [drawAt, drawAt + 0.01, drawAt + 0.045, drawAt + 0.055, 1]
        )}
      />
    </div>
  );
}

export function AiFollowUpVisual() {
  const reduce = useReducedMotion();

  return (
    <motion.div
      aria-hidden="true"
      animate={reduce ? undefined : { y: [0, -7, 0] }}
      transition={reduce ? undefined : { duration: 7, repeat: Infinity, ease: 'easeInOut' }}
      className="relative w-full max-w-md rounded-2xl border border-white/10 bg-white/[0.04] p-6 shadow-2xl shadow-indigo-950/50 backdrop-blur"
    >
      {/* Your email goes out */}
      <motion.div
        className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.05] px-4 py-3"
        {...seq(reduce, { opacity: [0, 1, 1, 0], y: [12, 0, 0, -6] }, [0.02, 0.07, 0.93, 0.98])}
      >
        <div className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-indigo-500/15">
          <PaperPlaneTilt weight="bold" className="h-[18px] w-[18px] text-indigo-300" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-zinc-100">Your email, sent</p>
          <p className="text-xs text-zinc-500">Waiting on a reply</p>
        </div>
      </motion.div>

      <Connector reduce={reduce} drawAt={0.07} />

      {/* AI takes over */}
      <motion.div
        className="flex items-center gap-3"
        {...seq(reduce, { opacity: [0, 1, 1, 0], y: [10, 0, 0, -6] }, [0.1, 0.15, 0.93, 0.98])}
      >
        <div className="relative flex h-[42px] w-[42px] flex-none items-center justify-center">
          <motion.div
            className="absolute inset-0 rounded-full border border-indigo-400/50"
            animate={reduce ? undefined : { scale: [1, 1.5], opacity: [0.6, 0] }}
            transition={reduce ? undefined : { duration: 2, repeat: Infinity, ease: 'easeOut' }}
          />
          <motion.div
            className="flex h-[42px] w-[42px] items-center justify-center rounded-full bg-indigo-600 shadow-lg shadow-indigo-600/40"
            {...seq(
              reduce,
              { scale: [1, 1, 1.14, 1, 1, 1.14, 1, 1] },
              [0, 0.18, 0.21, 0.24, 0.36, 0.39, 0.42, 1]
            )}
          >
            <Robot weight="fill" className="h-5 w-5 text-white" />
          </motion.div>
        </div>
        <div>
          <p className="text-sm font-medium text-zinc-100">AI on the chase</p>
          <motion.div
            className="mt-1 flex gap-1"
            {...seq(reduce, { opacity: [0, 1, 1, 0, 0] }, [0.15, 0.19, 0.55, 0.6, 1])}
          >
            {[0, 1, 2].map((i) => (
              <motion.span
                key={i}
                className="h-1.5 w-1.5 rounded-full bg-indigo-400"
                animate={reduce ? undefined : { y: [0, -3, 0] }}
                transition={
                  reduce ? undefined : { duration: 0.9, repeat: Infinity, ease: 'easeInOut', delay: i * 0.15 }
                }
              />
            ))}
          </motion.div>
        </div>
      </motion.div>

      {/* Follow-ups fired */}
      <div className="ml-12 mt-3 space-y-2">
        <motion.div
          className="flex items-center justify-between rounded-lg border border-indigo-400/20 bg-indigo-500/10 px-3 py-2"
          {...seq(reduce, { opacity: [0, 1, 1, 0], x: [-10, 0, 0, 0] }, [0.24, 0.29, 0.93, 0.98])}
        >
          <span className="text-xs font-medium text-indigo-200">Follow-up sent</span>
          <span className="text-xs text-zinc-500">3 days later</span>
        </motion.div>
        <motion.div
          className="flex items-center justify-between rounded-lg border border-indigo-400/20 bg-indigo-500/10 px-3 py-2"
          {...seq(reduce, { opacity: [0, 1, 1, 0], x: [-10, 0, 0, 0] }, [0.42, 0.47, 0.93, 0.98])}
        >
          <span className="text-xs font-medium text-indigo-200">Follow-up sent</span>
          <span className="text-xs text-zinc-500">7 days later</span>
        </motion.div>
      </div>

      <Connector reduce={reduce} drawAt={0.52} />

      {/* Reply arrives, verified */}
      <motion.div
        className="flex items-center gap-3 rounded-xl border border-emerald-400/20 bg-emerald-500/[0.07] px-4 py-3"
        {...seq(reduce, { opacity: [0, 1, 1, 0], y: [14, 0, 0, -6] }, [0.58, 0.64, 0.93, 0.98])}
      >
        <div className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-emerald-500/15">
          <EnvelopeOpen weight="bold" className="h-[18px] w-[18px] text-emerald-300" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-zinc-100">Reply received</p>
          <p className="text-xs text-zinc-500">Verified and resolved</p>
        </div>
        <motion.div {...seq(reduce, { scale: [0, 0, 1.25, 1, 1] }, [0, 0.63, 0.68, 0.71, 1])}>
          <CheckCircle weight="fill" className="h-6 w-6 text-emerald-400" />
        </motion.div>
      </motion.div>
    </motion.div>
  );
}
